import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Plus, Trash2, Upload, X, Save, ArrowLeft, AlertTriangle,
  CheckCircle, ChevronDown, User, MapPin, Globe, Briefcase, Search,
  FileText, Download, UserCheck, Link2, Link2Off, Mail, RefreshCw, Loader2,
  ShieldCheck, Tag, Pencil, Building2, ListChecks, Settings,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useHQ } from '../contexts/HQContext';
import { useTheme } from '../contexts/ThemeContext';
import TeamIcon from '../components/TeamIcon';
import TeamIconPicker from '../components/TeamIconPicker';
import type { Team } from '../types/employee';
import { fetchAllTeams, createTeam, updateTeam, deleteTeam } from '../utils/api';
import { supabase } from '../lib/supabase';
import { geocodeCityCountry } from '../utils/geocode';
import LocationPreview from '../components/LocationPreview';
import { captureMutationError } from '../utils/errorReporting';
import { getBasemapUrl, getBasemapAttribution } from '../lib/basemap';
import { useAuth } from '../contexts/AuthContext';
import { useEmployee } from '../contexts/EmployeeContext';
import type { EmployeeRow, ContractorRow } from '../lib/database.types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  name: string; role: string; department: string; employer: string;
  email: string; phone: string; bio: string; start_date: string;
  skills: string; languages: string;
  city: string; country: string; latitude: string; longitude: string; timezone: string;
  avatar_url: string; avatar_hover_url: string;
  social_linkedin: string; social_twitter: string; social_github: string; social_slack: string;
}

const emptyForm = (): FormState => ({
  name: '', role: '', department: '', employer: '', email: '', phone: '', bio: '',
  start_date: '', skills: '', languages: '',
  city: '', country: '', latitude: '', longitude: '', timezone: 'UTC',
  avatar_url: '', avatar_hover_url: '',
  social_linkedin: '', social_twitter: '', social_github: '', social_slack: '',
});

interface CsvRow { [key: string]: string }
type CsvMapping = { [K in keyof FormState]?: string };
type CsvDefaults = { [K in keyof FormState]?: string };

const FIELD_LABELS: { key: keyof FormState; label: string; required?: boolean }[] = [
  { key: 'name', label: 'Full name', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'role', label: 'Role / Title', required: true },
  { key: 'department', label: 'Department', required: true },
  { key: 'employer', label: 'Employer' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country', required: true },
  { key: 'timezone', label: 'Timezone' },
  { key: 'phone', label: 'Phone' },
  { key: 'bio', label: 'Bio' },
  { key: 'start_date', label: 'Start date' },
  { key: 'skills', label: 'Skills (comma-sep)' },
  { key: 'languages', label: 'Languages (comma-sep)' },
  { key: 'latitude', label: 'Latitude' },
  { key: 'longitude', label: 'Longitude' },
  { key: 'avatar_url', label: 'Avatar URL' },
  { key: 'avatar_hover_url', label: 'Hover Avatar URL' },
  { key: 'social_linkedin', label: 'LinkedIn handle' },
  { key: 'social_twitter', label: 'Twitter handle' },
  { key: 'social_github', label: 'GitHub username' },
  { key: 'social_slack', label: 'Slack name' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formToRow(form: FormState) {
  return {
    name: form.name.trim(),
    role: form.role.trim(),
    department: form.department.trim(),
    employer: form.employer.trim(),
    email: form.email.trim(),
    phone: form.phone.trim() || null,
    bio: form.bio.trim(),
    start_date: form.start_date || null,
    skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
    languages: form.languages.split(',').map(s => s.trim()).filter(Boolean),
    city: form.city.trim(),
    country: form.country.trim(),
    latitude: form.latitude ? parseFloat(form.latitude) : null,
    longitude: form.longitude ? parseFloat(form.longitude) : null,
    timezone: form.timezone.trim() || 'UTC',
    avatar_url: form.avatar_url.trim() || null,
    avatar_hover_url: form.avatar_hover_url.trim() || null,
    social_media: {
      linkedin: form.social_linkedin.trim() || undefined,
      twitter: form.social_twitter.trim() || undefined,
      github: form.social_github.trim() || undefined,
      slack: form.social_slack.trim() || undefined,
    },
    profile_complete: true,
  };
}

function generateSlug(name: string, existing: string[]): string {
  const base = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  let slug = base;
  let i = 2;
  while (existing.includes(slug)) { slug = `${base}-${i++}`; }
  return slug;
}

function parseCSV(text: string): { headers: string[]; rows: CsvRow[] } {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const rows = lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; continue; }
      if (ch === ',' && !inQuotes) { values.push(current); current = ''; continue; }
      current += ch;
    }
    values.push(current);
    return Object.fromEntries(headers.map((h, i) => [h, (values[i] ?? '').trim()]));
  }).filter(row => Object.values(row).some(v => v));
  return { headers, rows };
}

function autoMap(headers: string[]): CsvMapping {
  const mapping: CsvMapping = {};
  const lc = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const field of FIELD_LABELS) {
    const match = headers.find(h => lc(h) === lc(field.label) || lc(h) === lc(field.key));
    if (match) mapping[field.key] = match;
  }
  return mapping;
}

function inferEmployerFromEmail(email: string): string {
  const domain = email.split('@')[1]?.toLowerCase();
  if (domain === 'stackblitz.com') return 'StackBlitz';
  return '';
}

function rowToForm(csvRow: CsvRow, mapping: CsvMapping, defaults: CsvDefaults): FormState {
  const form = emptyForm();
  for (const field of FIELD_LABELS) {
    const header = mapping[field.key];
    if (header && header !== '__default__' && csvRow[header] !== undefined) {
      (form as Record<string, string>)[field.key] = csvRow[header];
    } else if (defaults[field.key]) {
      (form as Record<string, string>)[field.key] = defaults[field.key]!;
    }
  }
  if (!form.employer && form.email) {
    form.employer = inferEmployerFromEmail(form.email);
  }
  return form;
}

// ─── Main Component ───────────────────────────────────────────────────────────

type Tab = 'employees' | 'contractors' | 'admins' | 'teams' | 'hq' | 'debug';
type View = 'list' | 'add' | 'csv-upload' | 'csv-map' | 'csv-preview';

interface QuickEditState {
  id: string;
  role: string;
  department: string;
  start_date: string;
  city: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
  coordinates_manual: boolean;
}

interface BulkEditFields {
  role: string;
  department: string;
  start_date: string;
}

// ─── Quick Edit Modal ─────────────────────────────────────────────────────────

interface QuickEditModalProps {
  state: QuickEditState;
  isLoading: boolean;
  error: string | null;
  onChange: (patch: Partial<QuickEditState>) => void;
  onSave: () => void;
  onClose: () => void;
}

const QuickEditModal = ({ state, isLoading, error, onChange, onSave, onClose }: QuickEditModalProps) => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  const handleGeocode = async () => {
    if (!state.city.trim() || !state.country.trim()) return;
    setIsGeocoding(true);
    setGeocodeError(null);
    try {
      const result = await geocodeCityCountry(state.city.trim(), state.country.trim());
      if (result) {
        onChange({
          latitude: String(result.lat),
          longitude: String(result.lon),
          timezone: result.timezone ?? state.timezone,
          coordinates_manual: false,
        });
      } else {
        setGeocodeError('Location not found. Try adjusting city or country.');
      }
    } catch {
      setGeocodeError('Geocoding failed.');
    } finally {
      setIsGeocoding(false);
    }
  };

  const lat = parseFloat(state.latitude);
  const lng = parseFloat(state.longitude);
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg p-6 space-y-5 animate-fade-in overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-bolt-text">Edit employee</h3>
          <button onClick={onClose} className="text-bolt-textSubtle hover:text-bolt-text transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Role & department */}
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-bolt-textSubtle">Job details</p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Role" required>
              <input
                type="text"
                value={state.role}
                onChange={e => onChange({ role: e.target.value })}
                className="form-input"
                required
              />
            </FormField>
            <FormField label="Department" required>
              <input
                type="text"
                value={state.department}
                onChange={e => onChange({ department: e.target.value })}
                className="form-input"
                required
              />
            </FormField>
          </div>
          <FormField label="Start date">
            <input
              type="date"
              value={state.start_date}
              onChange={e => onChange({ start_date: e.target.value })}
              className="form-input"
            />
          </FormField>
        </div>

        {/* Location */}
        <div className="space-y-3 border-t border-bolt-border pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-bolt-textSubtle flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> Location
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="City">
              <input
                type="text"
                value={state.city}
                onChange={e => onChange({ city: e.target.value })}
                className="form-input"
              />
            </FormField>
            <FormField label="Country">
              <input
                type="text"
                value={state.country}
                onChange={e => onChange({ country: e.target.value })}
                className="form-input"
              />
            </FormField>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <FormField label="Timezone">
                <input
                  type="text"
                  value={state.timezone}
                  onChange={e => onChange({ timezone: e.target.value })}
                  className="form-input"
                  placeholder="e.g. America/New_York"
                />
              </FormField>
            </div>
            <button
              type="button"
              onClick={handleGeocode}
              disabled={isGeocoding || !state.city.trim() || !state.country.trim()}
              className="btn btn-secondary text-xs px-3 py-2 gap-1.5 flex-shrink-0 mb-0.5"
              title="Auto-fill coordinates from city & country"
            >
              {isGeocoding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
              Geocode
            </button>
          </div>
          {geocodeError && (
            <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
              {geocodeError}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Latitude">
              <input
                type="number"
                step="any"
                value={state.latitude}
                onChange={e => onChange({ latitude: e.target.value, coordinates_manual: true })}
                className="form-input"
                placeholder="e.g. 51.5074"
              />
            </FormField>
            <FormField label="Longitude">
              <input
                type="number"
                step="any"
                value={state.longitude}
                onChange={e => onChange({ longitude: e.target.value, coordinates_manual: true })}
                className="form-input"
                placeholder="e.g. -0.1278"
              />
            </FormField>
          </div>
          {hasCoords && (
            <div className="rounded-xl overflow-hidden border border-bolt-border h-40">
              <LocationPreview lat={lat} lon={lng} country={state.country} />
            </div>
          )}
          {state.coordinates_manual && hasCoords && (
            <p className="text-xs text-amber-400 flex items-center gap-1.5">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              Coordinates set manually
            </p>
          )}
        </div>

        {error && (
          <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn btn-secondary text-xs px-4 py-2">Cancel</button>
          <button
            onClick={onSave}
            disabled={isLoading || !state.role.trim() || !state.department.trim()}
            className="btn btn-primary text-xs px-4 py-2 gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {isLoading ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Admin Page ───────────────────────────────────────────────────────────────

const AdminPage = () => {
  const { isAdmin } = useAuth();
  const { employees, reloadEmployees } = useEmployee();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('employees');
  const [view, setView] = useState<View>('list');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<EmployeeRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Quick edit
  const [quickEdit, setQuickEdit] = useState<QuickEditState | null>(null);
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [quickEditError, setQuickEditError] = useState<string | null>(null);

  // Bulk edit
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkFields, setBulkFields] = useState<BulkEditFields>({ role: '', department: '', start_date: '' });
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState<string | null>(null);

  const [adminEmails, setAdminEmails] = useState<Set<string>>(new Set());

  const [csvHeaders, setCsvHeaders] = useState<string[]>([]); 
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [csvMapping, setCsvMapping] = useState<CsvMapping>({});
  const [csvDefaults, setCsvDefaults] = useState<CsvDefaults>({});
  const [csvPreview, setCsvPreview] = useState<{ form: FormState; valid: boolean; error?: string }[]>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<{ imported: number; skipped: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data: roleRows } = await supabase.from('user_roles').select('user_id');
      if (!roleRows?.length) return;
      const { data: authUsers } = await supabase.rpc('get_all_auth_users');
      if (!authUsers) return;
      const userIds = new Set(roleRows.map(r => r.user_id));
      const emails = new Set(
        (authUsers as { id: string; email: string }[])
          .filter(u => userIds.has(u.id))
          .map(u => u.email.toLowerCase())
      );
      setAdminEmails(emails);
    })();
  }, []);

  if (!isAdmin) {
    return (
      <div className="text-center py-24">
        <Shield className="h-10 w-10 text-bolt-textSubtle mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-bolt-text mb-2">Admin only</h2>
        <p className="text-sm text-bolt-textMuted">You don't have permission to view this page.</p>
      </div>
    );
  }

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return !q || e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q) ||
      e.department.toLowerCase().includes(q) || e.location.city.toLowerCase().includes(q);
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setIsSaving(true);
    try {
      const existingSlugs = employees.map(em => em.id);
      const slug = generateSlug(form.name, existingSlugs);
      const { error } = await supabase.from('employees').insert({ ...formToRow(form), slug });
      if (error) throw error;
      await reloadEmployees();
      setView('list');
      setForm(emptyForm());
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'addEmployee', table: 'employees' });
      setSaveError((err as { message?: string })?.message ?? String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const openQuickEdit = async (emp: ReturnType<typeof useEmployee>['employees'][number]) => {
    const { data } = await supabase.from('employees').select('id, role, department, start_date, city, country, latitude, longitude, timezone, coordinates_manual').eq('slug', emp.id).maybeSingle();
    if (data) {
      setQuickEdit({
        id: data.id,
        role: data.role,
        department: data.department,
        start_date: data.start_date ?? '',
        city: data.city ?? '',
        country: data.country ?? '',
        latitude: data.latitude != null ? String(data.latitude) : '',
        longitude: data.longitude != null ? String(data.longitude) : '',
        timezone: data.timezone ?? 'UTC',
        coordinates_manual: data.coordinates_manual ?? false,
      });
      setQuickEditError(null);
    }
  };

  const handleQuickSave = async () => {
    if (!quickEdit) return;
    setIsQuickSaving(true);
    setQuickEditError(null);
    try {
      const { error } = await supabase.from('employees').update({
        role: quickEdit.role.trim(),
        department: quickEdit.department.trim(),
        start_date: quickEdit.start_date || null,
        city: quickEdit.city.trim(),
        country: quickEdit.country.trim(),
        latitude: quickEdit.latitude ? parseFloat(quickEdit.latitude) : null,
        longitude: quickEdit.longitude ? parseFloat(quickEdit.longitude) : null,
        timezone: quickEdit.timezone.trim() || 'UTC',
        coordinates_manual: quickEdit.coordinates_manual,
      }).eq('id', quickEdit.id);
      if (error) throw error;
      await reloadEmployees();
      setQuickEdit(null);
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'quickEditEmployee', table: 'employees', entityId: quickEdit.id });
      setQuickEditError((err as { message?: string })?.message ?? String(err));
    } finally {
      setIsQuickSaving(false);
    }
  };

  const toggleBulkMode = () => {
    setBulkMode(v => !v);
    setSelectedIds(new Set());
    setBulkFields({ role: '', department: '', start_date: '' });
    setBulkSuccess(null);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(e => e.id)));
    }
  };

  const handleBulkApply = async () => {
    if (selectedIds.size === 0) return;
    const update: Record<string, string | null> = {};
    if (bulkFields.role.trim()) update.role = bulkFields.role.trim();
    if (bulkFields.department.trim()) update.department = bulkFields.department.trim();
    if (bulkFields.start_date) update.start_date = bulkFields.start_date;
    if (Object.keys(update).length === 0) return;
    setIsBulkSaving(true);
    setBulkSuccess(null);
    try {
      const ids = await Promise.all(
        [...selectedIds].map(async slug => {
          const { data } = await supabase.from('employees').select('id').eq('slug', slug).maybeSingle();
          return data?.id;
        })
      );
      const validIds = ids.filter(Boolean) as string[];
      const { error } = await supabase.from('employees').update(update).in('id', validIds);
      if (error) throw error;
      await reloadEmployees();
      setBulkSuccess(`Updated ${validIds.length} employee${validIds.length !== 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      setBulkFields({ role: '', department: '', start_date: '' });
      setTimeout(() => { setBulkMode(false); setBulkSuccess(null); }, 2000);
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'bulkUpdateEmployees', table: 'employees' });
      setBulkSuccess(null);
      setSaveError((err as { message?: string })?.message ?? String(err));
    } finally {
      setIsBulkSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase.from('employees').delete().eq('id', confirmDelete.id);
      if (error) throw error;
      await reloadEmployees();
      setConfirmDelete(null);
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'deleteEmployee', table: 'employees', entityId: confirmDelete.id });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const { headers, rows } = parseCSV(ev.target?.result as string);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setCsvMapping(autoMap(headers));
      setView('csv-map');
    };
    reader.readAsText(file);
  };

  const handleBuildPreview = () => {
    const preview = csvRows.map(row => {
      const f = rowToForm(row, csvMapping, csvDefaults);
      const missing = FIELD_LABELS.filter(fl => fl.required && !f[fl.key as keyof FormState]);
      if (!f.employer) missing.push({ key: 'employer', label: 'Employer (cannot be inferred from email)' });
      return {
        form: f,
        valid: missing.length === 0,
        error: missing.length ? `Missing: ${missing.map(m => m.label).join(', ')}` : undefined,
      };
    });
    setCsvPreview(preview);
    setCsvResult(null);
    setView('csv-preview');
  };

  const [csvImportProgress, setCsvImportProgress] = useState<{ current: number; total: number; geocoding: boolean } | null>(null);

  const handleImport = async () => {
    setCsvImporting(true);
    setCsvResult(null);
    let imported = 0;
    let skipped = 0;
    const validItems = csvPreview.filter(r => r.valid);
    const existingSlugs = employees.map(em => em.id);
    const newSlugs: string[] = [...existingSlugs];
    for (let i = 0; i < csvPreview.length; i++) {
      const item = csvPreview[i];
      if (!item.valid) { skipped++; continue; }
      const form = { ...item.form };
      const needsGeocode = (!form.latitude || !form.longitude) && form.country;
      setCsvImportProgress({ current: i + 1, total: validItems.length + csvPreview.filter(r => !r.valid).length, geocoding: !!needsGeocode });
      if (needsGeocode) {
        try {
          const geo = await geocodeCityCountry(form.city, form.country);
          if (geo) {
            form.latitude = geo.lat;
            form.longitude = geo.lon;
            if (geo.timezone && (!form.timezone || form.timezone === 'UTC')) form.timezone = geo.timezone;
          }
        } catch { /* geocode failure is non-fatal */ }
      }
      try {
        const slug = generateSlug(form.name, newSlugs);
        newSlugs.push(slug);
        const { error } = await supabase.from('employees').insert({ ...formToRow(form), slug });
        if (error) { skipped++; } else { imported++; }
      } catch (err: unknown) {
        captureMutationError(err, { operation: 'csvImportEmployee', table: 'employees' });
        skipped++;
      }
    }
    await reloadEmployees();
    setCsvImportProgress(null);
    setCsvResult({ imported, skipped });
    setCsvImporting(false);
  };

  const resetCsv = () => {
    setCsvHeaders([]); setCsvRows([]); setCsvMapping({}); setCsvDefaults({});
    setCsvPreview([]); setCsvResult(null);
    if (fileRef.current) fileRef.current.value = '';
    setView('list');
  };

  const downloadTemplate = () => {
    const headers = FIELD_LABELS.map(f => f.label);
    const example = ['Jane Smith', 'jane@example.com', 'Software Engineer', 'Engineering', 'Acme Corp',
      'New York', 'USA', 'America/New_York', '+1 555 0100', 'Bio here', '2024-01-15',
      'TypeScript, React', 'English', '40.7128', '-74.0060', '', '', 'janesmith', 'janesmith', 'janesmith', 'jane'];
    const csv = [headers.join(','), example.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'employees-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const switchTab = (t: Tab) => {
    setTab(t);
    setView('list');
    setSaveError(null);
    setForm(emptyForm());
    setSearch('');
  };

  return (
    <div className="animate-fade-in space-y-5 max-w-5xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        {view !== 'list' ? (
          <button
            onClick={() => { setView('list'); setSaveError(null); setForm(emptyForm()); }}
            className="flex items-center gap-1.5 text-sm text-bolt-textMuted hover:text-bolt-text transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        ) : (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-bolt-textMuted hover:text-bolt-text transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Directory
          </button>
        )}
        <div className="flex items-center gap-1.5 text-xs text-bolt-textSubtle">
          <Shield className="h-3.5 w-3.5" />
          Admin panel
        </div>
      </div>

      {/* Tabs */}
      {view === 'list' && (
        <div className="flex items-center gap-1 border-b border-bolt-border">
          <TabButton active={tab === 'employees'} onClick={() => switchTab('employees')}>
            <User className="h-3.5 w-3.5" />
            Employees
          </TabButton>
          <TabButton active={tab === 'contractors'} onClick={() => switchTab('contractors')}>
            <UserCheck className="h-3.5 w-3.5" />
            Contractors
          </TabButton>
          <TabButton active={tab === 'admins'} onClick={() => switchTab('admins')}>
            <ShieldCheck className="h-3.5 w-3.5" />
            Admins
          </TabButton>
          <TabButton active={tab === 'teams'} onClick={() => switchTab('teams')}>
            <Tag className="h-3.5 w-3.5" />
            Teams
          </TabButton>
          <TabButton active={tab === 'hq'} onClick={() => switchTab('hq')}>
            <Building2 className="h-3.5 w-3.5" />
            HQ
          </TabButton>
          <TabButton active={tab === 'debug'} onClick={() => switchTab('debug')}>
            <Settings className="h-3.5 w-3.5" />
            Debug
          </TabButton>
        </div>
      )}

      {/* ── EMPLOYEES TAB ──────────────────────────────────────────────────── */}
      {tab === 'employees' && (
        <>
          {view === 'list' && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h1 className="text-lg font-semibold text-bolt-text">Employees</h1>
                  <p className="text-xs text-bolt-textMuted">{employees.length} total</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={downloadTemplate} className="btn btn-secondary text-xs px-3 py-1.5 gap-1.5">
                    <Download className="h-3.5 w-3.5" />
                    CSV template
                  </button>
                  <button onClick={() => setView('csv-upload')} className="btn btn-secondary text-xs px-3 py-1.5 gap-1.5">
                    <Upload className="h-3.5 w-3.5" />
                    Import CSV
                  </button>
                  <button
                    onClick={toggleBulkMode}
                    className={`btn text-xs px-3 py-1.5 gap-1.5 ${
                      bulkMode
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    <ListChecks className="h-3.5 w-3.5" />
                    {bulkMode
                      ? selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Cancel'
                      : 'Bulk edit'
                    }
                  </button>
                  <button
                    onClick={() => { setForm(emptyForm()); setSaveError(null); setView('add'); }}
                    className="btn btn-primary text-xs px-3 py-1.5 gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add employee
                  </button>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-4 py-3 border-b border-bolt-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                    <input
                      className="form-input pl-9 w-full sm:w-72"
                      placeholder="Search employees..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-bolt-border">
                        {bulkMode && (
                          <th className="pl-4 py-3 w-8">
                            <input
                              type="checkbox"
                              checked={filtered.length > 0 && selectedIds.size === filtered.length}
                              onChange={toggleSelectAll}
                              className="w-3.5 h-3.5 rounded accent-primary-500 cursor-pointer"
                            />
                          </th>
                        )}
                        <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Name</th>
                        <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden sm:table-cell">Role</th>
                        <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden md:table-cell">Department</th>
                        <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden lg:table-cell">Location</th>
                        <th className="w-16 px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={bulkMode ? 6 : 5} className="px-4 py-10 text-center text-bolt-textSubtle">
                            {search ? 'No matches found.' : 'No employees yet.'}
                          </td>
                        </tr>
                      )}
                      {filtered.map(emp => (
                        <tr
                          key={emp.id}
                          className={`border-b border-bolt-border hover:bg-bolt-surfaceHover transition-colors group ${
                            bulkMode && selectedIds.has(emp.id) ? 'bg-primary-600/5' : ''
                          }`}
                          onClick={bulkMode ? () => toggleSelect(emp.id) : undefined}
                          style={bulkMode ? { cursor: 'pointer' } : undefined}
                        >
                          {bulkMode && (
                            <td className="pl-4 py-3 w-8" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(emp.id)}
                                onChange={() => toggleSelect(emp.id)}
                                className="w-3.5 h-3.5 rounded accent-primary-500 cursor-pointer"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <img src={emp.avatar} alt={emp.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium text-bolt-text truncate">{emp.name}</p>
                                  {adminEmails.has(emp.email.toLowerCase()) && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary-500/15 text-primary-300 border border-primary-500/20 flex-shrink-0">
                                      <Shield className="h-2.5 w-2.5" />
                                      Admin
                                    </span>
                                  )}
                                </div>
                                <p className="text-bolt-textSubtle truncate">{emp.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-bolt-textMuted hidden sm:table-cell">{emp.role}</td>
                          <td className="px-4 py-3 text-bolt-textMuted hidden md:table-cell">{emp.department}</td>
                          <td className="px-4 py-3 text-bolt-textMuted hidden lg:table-cell">
                            {[emp.location.city, emp.location.country].filter(Boolean).join(', ')}
                          </td>
                          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                            {!bulkMode && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openQuickEdit(emp)}
                                  className="p-1.5 rounded text-bolt-textSubtle hover:text-bolt-text hover:bg-bolt-bg transition-all"
                                  title="Edit employee"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={async () => {
                                    const { data } = await supabase.from('employees').select('*').eq('slug', emp.id).maybeSingle();
                                    if (data) setConfirmDelete(data);
                                  }}
                                  className="p-1.5 rounded text-bolt-textSubtle hover:text-error-400 hover:bg-error-500/10 transition-all"
                                  title="Delete employee"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Bulk edit action bar */}
                {bulkMode && (
                  <div className="border-t border-primary-600/30 bg-primary-600/5 px-4 py-4 space-y-3">
                    {bulkSuccess ? (
                      <div className="flex items-center gap-2 text-success-400 text-xs">
                        <CheckCircle className="h-4 w-4" />
                        {bulkSuccess}
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-bolt-text">
                          {selectedIds.size === 0
                            ? 'Select employees to edit'
                            : `Edit fields for ${selectedIds.size} employee${selectedIds.size !== 1 ? 's' : ''} — leave blank to keep unchanged`
                          }
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-bolt-textMuted mb-1">Role</label>
                            <input
                              type="text"
                              value={bulkFields.role}
                              onChange={e => setBulkFields(f => ({ ...f, role: e.target.value }))}
                              placeholder="Leave blank to keep"
                              className="form-input w-full text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-bolt-textMuted mb-1">Department</label>
                            <input
                              type="text"
                              value={bulkFields.department}
                              onChange={e => setBulkFields(f => ({ ...f, department: e.target.value }))}
                              placeholder="Leave blank to keep"
                              className="form-input w-full text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-bolt-textMuted mb-1">Start date</label>
                            <input
                              type="date"
                              value={bulkFields.start_date}
                              onChange={e => setBulkFields(f => ({ ...f, start_date: e.target.value }))}
                              className="form-input w-full text-xs"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <button onClick={toggleBulkMode} className="btn btn-secondary text-xs px-3 py-1.5">
                            Cancel
                          </button>
                          <button
                            onClick={handleBulkApply}
                            disabled={
                              isBulkSaving ||
                              selectedIds.size === 0 ||
                              (!bulkFields.role.trim() && !bulkFields.department.trim() && !bulkFields.start_date)
                            }
                            className="btn btn-primary text-xs px-4 py-1.5 gap-1.5"
                          >
                            <Save className="h-3.5 w-3.5" />
                            {isBulkSaving ? 'Applying...' : `Apply to ${selectedIds.size} employee${selectedIds.size !== 1 ? 's' : ''}`}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {view === 'add' && (
            <div className="max-w-2xl mx-auto">
              <div className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-bolt-border flex items-center gap-2">
                  <Plus className="h-4 w-4 text-bolt-textSubtle" />
                  <h2 className="text-sm font-semibold text-bolt-text">Add employee</h2>
                </div>
                <form onSubmit={handleAdd} className="p-5 space-y-6">
                  <AdminFormFields form={form} setForm={setForm} />
                  {saveError && (
                    <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
                      {saveError}
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button type="button" onClick={() => { setView('list'); setForm(emptyForm()); }} className="btn btn-secondary text-xs px-4 py-2">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="btn btn-primary text-xs px-4 py-2 gap-1.5">
                      <Save className="h-3.5 w-3.5" />
                      {isSaving ? 'Saving...' : 'Save employee'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {view === 'csv-upload' && (
            <div className="max-w-xl mx-auto">
              <div className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-bolt-border flex items-center gap-2">
                  <Upload className="h-4 w-4 text-bolt-textSubtle" />
                  <h2 className="text-sm font-semibold text-bolt-text">Import from CSV</h2>
                </div>
                <div className="p-6 space-y-5">
                  <p className="text-sm text-bolt-textMuted">
                    Upload a CSV file with employee data. Column headers will be auto-matched to fields.
                  </p>
                  <button onClick={downloadTemplate} className="flex items-center gap-2 text-xs text-primary-400 hover:text-primary-300 transition-colors">
                    <Download className="h-3.5 w-3.5" />
                    Download CSV template
                  </button>
                  <label className="block">
                    <div className="border-2 border-dashed border-bolt-border rounded-xl p-8 text-center hover:border-primary-600/50 transition-colors cursor-pointer">
                      <FileText className="h-8 w-8 text-bolt-textSubtle mx-auto mb-3" />
                      <p className="text-sm text-bolt-textMuted mb-1">Click to upload or drag & drop</p>
                      <p className="text-xs text-bolt-textSubtle">CSV files only</p>
                      <input ref={fileRef} type="file" accept=".csv,text/csv" className="sr-only" onChange={handleFileChange} />
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {view === 'csv-map' && (() => {
            const mappedFields = FIELD_LABELS.filter(f => csvMapping[f.key] && csvMapping[f.key] !== '__default__');
            const defaultedFields = FIELD_LABELS.filter(f => csvMapping[f.key] === '__default__');
            const unmappedFields = FIELD_LABELS.filter(f => !csvMapping[f.key]);

            const renderField = (field: typeof FIELD_LABELS[number]) => {
              const selected = csvMapping[field.key] ?? '';
              const isDefault = selected === '__default__';
              return (
                <div key={field.key}>
                  <label className="block text-xs text-bolt-textMuted mb-1">
                    {field.label}
                    {field.required && <span className="text-error-400 ml-0.5">*</span>}
                  </label>
                  <div className="relative">
                    <select
                      className="form-input w-full appearance-none pr-7 text-xs"
                      value={selected}
                      onChange={e => {
                        const val = e.target.value;
                        setCsvMapping(m => ({ ...m, [field.key]: val || undefined }));
                        if (val !== '__default__') {
                          setCsvDefaults(d => { const n = { ...d }; delete n[field.key]; return n; });
                        }
                      }}
                    >
                      <option value="">— skip —</option>
                      <option value="__default__">— use default —</option>
                      {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                  </div>
                  {isDefault && (
                    <input
                      type="text"
                      className="form-input w-full mt-1.5 text-xs"
                      placeholder={`Default value for ${field.label}`}
                      value={csvDefaults[field.key] ?? ''}
                      onChange={e => setCsvDefaults(d => ({ ...d, [field.key]: e.target.value }))}
                    />
                  )}
                </div>
              );
            };

            return (
              <div className="max-w-2xl mx-auto space-y-4">
                <div className="card overflow-hidden">
                  <div className="px-5 py-4 border-b border-bolt-border">
                    <h2 className="text-sm font-semibold text-bolt-text">Map columns</h2>
                    <p className="text-xs text-bolt-textMuted mt-0.5">
                      {csvRows.length} rows found · {mappedFields.length} of {FIELD_LABELS.length} fields mapped
                    </p>
                  </div>

                  {/* Mapped fields */}
                  {(mappedFields.length > 0 || defaultedFields.length > 0) && (
                    <div className="p-5 border-b border-bolt-border">
                      <p className="text-xs font-medium text-bolt-text mb-3">
                        Mapped
                        <span className="ml-1.5 text-bolt-textSubtle font-normal">({mappedFields.length + defaultedFields.length})</span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[...mappedFields, ...defaultedFields].map(renderField)}
                      </div>
                    </div>
                  )}

                  {/* Unmapped fields */}
                  {unmappedFields.length > 0 && (
                    <div className="p-5">
                      <p className="text-xs font-medium text-bolt-textMuted mb-3">
                        Unmapped
                        <span className="ml-1.5 font-normal">({unmappedFields.length})</span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {unmappedFields.map(renderField)}
                      </div>
                    </div>
                  )}


                  <div className="px-5 pb-5 flex justify-end gap-3">
                    <button onClick={resetCsv} className="btn btn-secondary text-xs px-4 py-2">Cancel</button>
                    <button onClick={handleBuildPreview} className="btn btn-primary text-xs px-4 py-2">Preview import</button>
                  </div>
                </div>
              </div>
            );
          })()}

          {view === 'csv-preview' && (
            <div className="space-y-4">
              <div className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-bolt-border flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-bolt-text">Preview</h2>
                    <p className="text-xs text-bolt-textMuted mt-0.5">
                      {csvPreview.filter(r => r.valid).length} valid ·{' '}
                      <span className="text-warning-400">{csvPreview.filter(r => !r.valid).length} will be skipped</span>
                    </p>
                  </div>
                  {csvResult && (
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle className="h-4 w-4 text-success-400" />
                      <span className="text-success-400">{csvResult.imported} imported</span>
                      {csvResult.skipped > 0 && <span className="text-bolt-textSubtle">{csvResult.skipped} skipped</span>}
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-bolt-border">
                        <th className="text-left px-4 py-2.5 text-bolt-textSubtle font-medium">Status</th>
                        <th className="text-left px-4 py-2.5 text-bolt-textSubtle font-medium">Name</th>
                        <th className="text-left px-4 py-2.5 text-bolt-textSubtle font-medium">Email</th>
                        <th className="text-left px-4 py-2.5 text-bolt-textSubtle font-medium hidden sm:table-cell">Role</th>
                        <th className="text-left px-4 py-2.5 text-bolt-textSubtle font-medium hidden md:table-cell">Department</th>
                        <th className="text-left px-4 py-2.5 text-bolt-textSubtle font-medium hidden lg:table-cell">Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, i) => (
                        <tr key={i} className={`border-b border-bolt-border/50 ${!row.valid ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2.5">
                            {row.valid
                              ? <CheckCircle className="h-3.5 w-3.5 text-success-400" />
                              : (
                                <div className="flex items-center gap-1" title={row.error}>
                                  <AlertTriangle className="h-3.5 w-3.5 text-warning-400 flex-shrink-0" />
                                  <span className="text-warning-400 truncate max-w-32 hidden sm:inline">{row.error}</span>
                                </div>
                              )
                            }
                          </td>
                          <td className="px-4 py-2.5 text-bolt-text font-medium">{row.form.name || '—'}</td>
                          <td className="px-4 py-2.5 text-bolt-textMuted">{row.form.email || '—'}</td>
                          <td className="px-4 py-2.5 text-bolt-textMuted hidden sm:table-cell">{row.form.role || '—'}</td>
                          <td className="px-4 py-2.5 text-bolt-textMuted hidden md:table-cell">{row.form.department || '—'}</td>
                          <td className="px-4 py-2.5 text-bolt-textMuted hidden lg:table-cell">
                            {[row.form.city, row.form.country].filter(Boolean).join(', ') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-5 py-4 border-t border-bolt-border flex flex-col gap-3">
                  {csvImportProgress && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs text-bolt-textMuted">
                        <span>
                          {csvImportProgress.geocoding ? 'Geocoding location...' : 'Inserting record...'}{' '}
                          <span className="text-bolt-text">{csvImportProgress.current} / {csvImportProgress.total}</span>
                        </span>
                        <span>{Math.round((csvImportProgress.current / csvImportProgress.total) * 100)}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-bolt-bg rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all duration-300"
                          style={{ width: `${(csvImportProgress.current / csvImportProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-3">
                  <button onClick={() => setView('csv-map')} className="btn btn-secondary text-xs px-4 py-2">Back</button>
                  {!csvResult ? (
                    <button
                      onClick={handleImport}
                      disabled={csvImporting || csvPreview.filter(r => r.valid).length === 0}
                      className="btn btn-primary text-xs px-4 py-2 gap-1.5"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {csvImporting ? 'Importing...' : `Import ${csvPreview.filter(r => r.valid).length} employees`}
                    </button>
                  ) : (
                    <button onClick={resetCsv} className="btn btn-primary text-xs px-4 py-2">Done</button>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── CONTRACTORS TAB ────────────────────────────────────────────────── */}
      {tab === 'contractors' && (
        <ContractorsTab employees={employees} />
      )}

      {/* ── ADMINS TAB ─────────────────────────────────────────────────────── */}
      {tab === 'admins' && (
        <AdminsTab />
      )}

      {/* ── TEAMS TAB ─────────────────────────────────────────────────────── */}
      {tab === 'teams' && (
        <TeamsTab />
      )}

      {/* ── HQ TAB ────────────────────────────────────────────────────────── */}
      {tab === 'hq' && (
        <HQTab />
      )}

      {/* ── DEBUG TAB ──────────────────────────────────────────────────────── */}
      {tab === 'debug' && (
        <DebugTab />
      )}

      {/* ── QUICK EDIT MODAL ────────────────────────────────────────────────── */}
      {quickEdit && (
        <QuickEditModal
          state={quickEdit}
          isLoading={isQuickSaving}
          error={quickEditError}
          onChange={patch => setQuickEdit(q => q ? { ...q, ...patch } : q)}
          onSave={handleQuickSave}
          onClose={() => setQuickEdit(null)}
        />
      )}

      {/* ── DELETE CONFIRM MODAL ─────────────────────────────────────────────── */}
      {confirmDelete && (
        <DeleteModal
          name={confirmDelete.name}
          description="This will permanently remove the employee profile. This action cannot be undone."
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

// ─── Admins Tab ───────────────────────────────────────────────────────────────

interface AdminUser {
  roleId: string;
  userId: string;
  email: string;
  role: 'admin' | 'hr';
  createdAt: string;
}

const AdminsTab = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteRole, setPromoteRole] = useState<'admin' | 'hr'>('admin');
  const [isPromoting, setIsPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [promoteSuccess, setPromoteSuccess] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<AdminUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const { data: roleRows } = await supabase
      .from('user_roles')
      .select('id, user_id, role, created_at')
      .order('created_at', { ascending: true });

    if (!roleRows || roleRows.length === 0) {
      setAdmins([]);
      setIsLoading(false);
      return;
    }

    const { data: authUsers } = await supabase.rpc('get_all_auth_users');

    const emailMap: Record<string, string> = {};
    if (authUsers) {
      for (const u of authUsers as { id: string; email: string }[]) {
        emailMap[u.id] = u.email;
      }
    }

    setAdmins(roleRows.map(r => ({
      roleId: r.id,
      userId: r.user_id,
      email: emailMap[r.user_id] ?? r.user_id,
      role: r.role as 'admin' | 'hr',
      createdAt: r.created_at,
    })));
    setIsLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePromote = async (e: React.FormEvent) => {
    e.preventDefault();
    setPromoteError(null);
    setPromoteSuccess(null);
    setIsPromoting(true);
    try {
      const email = promoteEmail.trim().toLowerCase();
      if (!email) throw new Error('Email is required');

      const { data: authUsers, error: rpcError } = await supabase.rpc('get_all_auth_users');
      if (rpcError) throw rpcError;

      const match = (authUsers as { id: string; email: string }[]).find(
        u => u.email.toLowerCase() === email
      );
      if (!match) throw new Error('No account found with that email address');

      const alreadyHasRole = admins.some(a => a.userId === match.id && a.role === promoteRole);
      if (alreadyHasRole) throw new Error(`${email} already has the ${promoteRole} role`);

      const { error } = await supabase.from('user_roles').insert({
        user_id: match.id,
        role: promoteRole,
      });
      if (error) throw error;

      setPromoteSuccess(`${email} has been promoted to ${promoteRole}`);
      setPromoteEmail('');
      await load();
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'promoteUser', table: 'user_roles' });
      setPromoteError((err as { message?: string })?.message ?? String(err));
    } finally {
      setIsPromoting(false);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setIsRemoving(true);
    const { error } = await supabase.from('user_roles').delete().eq('id', confirmRemove.roleId);
    if (error) {
      captureMutationError(error, { operation: 'removeAdminRole', table: 'user_roles', entityId: confirmRemove.roleId });
    }
    setConfirmRemove(null);
    setIsRemoving(false);
    await load();
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-bolt-text">Admins</h1>
        <p className="text-xs text-bolt-textMuted">Manage who has admin or HR access to this panel.</p>
      </div>

      {/* Promote form */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-bolt-border flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-bolt-textSubtle" />
          <h2 className="text-sm font-semibold text-bolt-text">Promote user</h2>
        </div>
        <form onSubmit={handlePromote} className="p-5 space-y-4">
          <p className="text-xs text-bolt-textMuted">
            Enter the email address of an existing account to grant them admin access.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
              <input
                type="email"
                value={promoteEmail}
                onChange={e => { setPromoteEmail(e.target.value); setPromoteError(null); setPromoteSuccess(null); }}
                className="form-input pl-9 w-full"
                placeholder="user@example.com"
                required
              />
            </div>
            <div className="relative sm:w-36">
              <select
                value={promoteRole}
                onChange={e => setPromoteRole(e.target.value as 'admin' | 'hr')}
                className="form-input w-full appearance-none pr-7 text-xs"
              >
                <option value="admin">Admin</option>
                <option value="hr">HR</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
            </div>
            <button type="submit" disabled={isPromoting} className="btn btn-primary text-xs px-4 py-2 gap-1.5 whitespace-nowrap">
              <ShieldCheck className="h-3.5 w-3.5" />
              {isPromoting ? 'Promoting...' : 'Promote'}
            </button>
          </div>
          {promoteError && (
            <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
              {promoteError}
            </p>
          )}
          {promoteSuccess && (
            <p className="text-xs text-success-400 bg-success-500/10 border border-success-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
              {promoteSuccess}
            </p>
          )}
        </form>
      </div>

      {/* Current admins table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-bolt-border">
          <h2 className="text-xs font-semibold text-bolt-textSubtle uppercase tracking-widest">Current admins</h2>
        </div>
        {isLoading ? (
          <div className="px-4 py-10 text-center text-bolt-textSubtle text-xs">Loading...</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bolt-border">
                <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Email</th>
                <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Role</th>
                <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden md:table-cell">Granted</th>
                <th className="w-12 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-bolt-textSubtle">No admins found.</td>
                </tr>
              )}
              {admins.map(a => (
                <tr key={a.roleId} className="border-b border-bolt-border/50 hover:bg-bolt-surfaceHover transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-primary-600/15 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="h-3.5 w-3.5 text-primary-400" />
                      </div>
                      <span className="font-medium text-bolt-text">{a.email}</span>
                      {a.userId === user?.id && (
                        <span className="text-bolt-textSubtle text-xs">(you)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      a.role === 'admin'
                        ? 'bg-primary-600/15 text-primary-400'
                        : 'bg-success-500/15 text-success-400'
                    }`}>
                      {a.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-bolt-textSubtle hidden md:table-cell">
                    {new Date(a.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {a.userId !== user?.id && (
                      <button
                        onClick={() => setConfirmRemove(a)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-bolt-textSubtle hover:text-error-400 hover:bg-error-500/10 transition-all"
                        title="Remove admin access"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmRemove && (
        <DeleteModal
          name={confirmRemove.email}
          description={`This will remove the ${confirmRemove.role} role from this user. They will lose access to the admin panel immediately.`}
          isDeleting={isRemoving}
          onConfirm={handleRemove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
};

// ─── Contractors Tab ──────────────────────────────────────────────────────────

interface ContractorWithStatus extends ContractorRow {
  hasAccount: boolean;
  linkedEmployee: EmployeeRow | null;
}

const ContractorsTab = ({ employees }: { employees: ReturnType<typeof useEmployee>['employees'] }) => {
  const [contractors, setContractors] = useState<ContractorWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmployeeId, setNewEmployeeId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Link employee modal
  const [linkTarget, setLinkTarget] = useState<ContractorWithStatus | null>(null);
  const [linkEmployeeId, setLinkEmployeeId] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  // Delete
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const { data } = await supabase.from('contractors').select('*').order('created_at', { ascending: false });
    if (!data) { setIsLoading(false); return; }

    // Check which emails have auth accounts
    const enriched: ContractorWithStatus[] = data.map(c => {
      const linked = employees.find(e => e.id === c.employee_id) ?? null;
      // We can't query auth.users directly from client — infer from employee having user_id
      // A contractor "has account" if their linked employee has a user_id, or if any employee
      // email matches (covers the case where they claimed a profile we can see)
      const matchByEmail = employees.find(e => e.email.toLowerCase() === c.email.toLowerCase());
      const hasAccount = !!(linked?.user_id || matchByEmail?.user_id);
      return { ...c, hasAccount, linkedEmployee: linked };
    });
    setContractors(enriched);
    setIsLoading(false);
  }, [employees]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setIsAdding(true);
    try {
      const email = newEmail.trim().toLowerCase();
      if (!email) throw new Error('Email is required');
      const { error } = await supabase.from('contractors').insert({
        email,
        employee_id: newEmployeeId || null,
      });
      if (error) throw error;
      setNewEmail('');
      setNewEmployeeId('');
      setShowAdd(false);
      await load();
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'addContractor', table: 'contractors' });
      setAddError((err as { message?: string })?.message ?? String(err));
    } finally {
      setIsAdding(false);
    }
  };

  const handleLink = async () => {
    if (!linkTarget) return;
    setIsLinking(true);
    const { error } = await supabase
      .from('contractors')
      .update({ employee_id: linkEmployeeId || null })
      .eq('id', linkTarget.id);
    if (error) {
      captureMutationError(error, { operation: 'linkContractor', table: 'contractors', entityId: linkTarget.id });
    } else {
      setLinkTarget(null);
      await load();
    }
    setIsLinking(false);
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    setIsDeleting(true);
    const { error } = await supabase.from('contractors').delete().eq('id', confirmDeleteId);
    if (error) {
      captureMutationError(error, { operation: 'deleteContractor', table: 'contractors', entityId: confirmDeleteId });
    }
    setConfirmDeleteId(null);
    setIsDeleting(false);
    await load();
  };

  const filtered = contractors.filter(c => {
    const q = search.toLowerCase();
    return !q || c.email.toLowerCase().includes(q) ||
      (c.linkedEmployee?.name ?? '').toLowerCase().includes(q);
  });

  // Employees without a contractor record and not yet linked
  const unlinkedEmployees = employees.filter(
    e => !contractors.some(c => c.employee_id === e.id)
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-bolt-text">Contractor allowlist</h1>
          <p className="text-xs text-bolt-textMuted">
            Contractors are created by admins and sign in with email and password.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="btn btn-secondary text-xs px-3 py-1.5 gap-1.5" title="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => { setShowAdd(true); setAddError(null); }}
            className="btn btn-primary text-xs px-3 py-1.5 gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add contractor
          </button>
        </div>
      </div>

      {/* Add contractor inline form */}
      {showAdd && (
        <div className="card p-4 border-primary-600/30 bg-primary-600/5">
          <h3 className="text-xs font-semibold text-bolt-text mb-3">Add contractor to allowlist</h3>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Email address" required>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    className="form-input pl-9"
                    placeholder="contractor@example.com"
                    required
                  />
                </div>
              </FormField>
              <FormField label="Link to employee profile" hint="optional">
                <div className="relative">
                  <select
                    className="form-input w-full appearance-none pr-7 text-xs"
                    value={newEmployeeId}
                    onChange={e => setNewEmployeeId(e.target.value)}
                  >
                    <option value="">— none —</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id}>{e.name} ({e.email})</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                </div>
              </FormField>
            </div>
            {addError && (
              <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
                {addError}
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="btn btn-secondary text-xs px-3 py-1.5">
                Cancel
              </button>
              <button type="submit" disabled={isAdding} className="btn btn-primary text-xs px-3 py-1.5 gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {isAdding ? 'Adding...' : 'Add to allowlist'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-bolt-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
            <input
              className="form-input pl-9 w-full sm:w-72"
              placeholder="Search contractors..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="px-4 py-10 text-center text-bolt-textSubtle text-xs">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-bolt-border">
                  <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden sm:table-cell">Linked profile</th>
                  <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Account status</th>
                  <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden md:table-cell">Added</th>
                  <th className="w-20 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-bolt-textSubtle">
                      {search ? 'No matches found.' : 'No contractors yet. Add one to allow sign-up.'}
                    </td>
                  </tr>
                )}
                {filtered.map(c => (
                  <tr key={c.id} className="border-b border-bolt-border/50 hover:bg-bolt-surfaceHover transition-colors group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-bolt-bg border border-bolt-border flex items-center justify-center flex-shrink-0">
                          <Mail className="h-3.5 w-3.5 text-bolt-textSubtle" />
                        </div>
                        <span className="font-medium text-bolt-text">{c.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      {c.linkedEmployee ? (
                        <div className="flex items-center gap-2">
                          <img src={c.linkedEmployee.avatar} alt={c.linkedEmployee.name} className="w-5 h-5 rounded-full object-cover" />
                          <span className="text-bolt-textMuted">{c.linkedEmployee.name}</span>
                        </div>
                      ) : (
                        <span className="text-bolt-textSubtle italic">Not linked</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.hasAccount ? (
                        <span className="inline-flex items-center gap-1.5 text-success-400">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Account created
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-bolt-textSubtle">
                          <div className="w-1.5 h-1.5 rounded-full bg-bolt-textSubtle" />
                          No account yet
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-bolt-textSubtle hidden md:table-cell">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => { setLinkTarget(c); setLinkEmployeeId(c.employee_id ?? ''); }}
                          className="p-1.5 rounded text-bolt-textSubtle hover:text-primary-400 hover:bg-primary-600/10 transition-all"
                          title={c.linkedEmployee ? 'Change linked profile' : 'Link employee profile'}
                        >
                          {c.linkedEmployee ? <Link2Off className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(c.id)}
                          className="p-1.5 rounded text-bolt-textSubtle hover:text-error-400 hover:bg-error-500/10 transition-all"
                          title="Remove from allowlist"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info callout */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-primary-600/5 border border-primary-600/20 text-xs text-bolt-textMuted">
        <Shield className="h-4 w-4 text-primary-400 flex-shrink-0 mt-0.5" />
        <p>
          Contractor accounts are created by admins via the Supabase dashboard. Once created, contractors
          sign in with their email and password. Linking a profile lets them claim their directory entry on first sign-in.
        </p>
      </div>

      {/* Link employee modal */}
      {linkTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-sm p-6 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-bolt-text">Link employee profile</h3>
              <button onClick={() => setLinkTarget(null)} className="text-bolt-textSubtle hover:text-bolt-text transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-bolt-textMuted">
              Linking a profile to <span className="font-medium text-bolt-text">{linkTarget.email}</span> lets them
              claim it automatically after signing up.
            </p>
            <div className="relative">
              <select
                className="form-input w-full appearance-none pr-7 text-xs"
                value={linkEmployeeId}
                onChange={e => setLinkEmployeeId(e.target.value)}
              >
                <option value="">— unlink —</option>
                {employees.map(e => (
                  <option key={e.id} value={e.id}>{e.name} ({e.email})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setLinkTarget(null)} className="btn btn-secondary text-xs px-4 py-2">Cancel</button>
              <button onClick={handleLink} disabled={isLinking} className="btn btn-primary text-xs px-4 py-2 gap-1.5">
                <Link2 className="h-3.5 w-3.5" />
                {isLinking ? 'Saving...' : 'Save link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDeleteId && (
        <DeleteModal
          name={contractors.find(c => c.id === confirmDeleteId)?.email ?? ''}
          description="This will remove the contractor from the allowlist. They will no longer be able to sign up or sign in with this email."
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};

// ─── Shared form components ───────────────────────────────────────────────────

const AdminFormFields = ({
  form, setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const geocodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));

    if (name === 'email') {
      const inferred = inferEmployerFromEmail(value);
      if (inferred) setForm(f => f.employer ? f : { ...f, employer: inferred });
    }

    if (name === 'city' || name === 'country') {
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
      geocodeTimer.current = setTimeout(() => {
        setForm(f => {
          const city = name === 'city' ? value : f.city;
          const country = name === 'country' ? value : f.country;
          if (!country) return f;
          setIsGeocoding(true);
          geocodeCityCountry(city, country).then(result => {
            if (result) {
              setForm(prev => ({
                ...prev,
                latitude: result.lat,
                longitude: result.lon,
                ...(result.timezone ? { timezone: result.timezone } : { timezone: editForm.timezone || 'UTC' }),
              }));
            }
            setIsGeocoding(false);
          });
          return f;
        });
      }, 600);
    }
  }, [setForm]);

  return (
    <>
      <FormSection icon={<User className="h-3.5 w-3.5" />} title="Basic info">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Full name" required>
            <input name="name" value={form.name} onChange={handleChange} className="form-input" required />
          </FormField>
          <FormField label="Role / Title" required>
            <input name="role" value={form.role} onChange={handleChange} className="form-input" required />
          </FormField>
          <FormField label="Department" required>
            <input name="department" value={form.department} onChange={handleChange} className="form-input" required />
          </FormField>
          <FormField label="Employer" required>
            <input name="employer" value={form.employer} onChange={handleChange} className="form-input" required />
          </FormField>
          <FormField label="Email" required>
            <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" required />
          </FormField>
          <FormField label="Phone">
            <input name="phone" type="tel" value={form.phone} onChange={handleChange} className="form-input" />
          </FormField>
          <FormField label="Start date">
            <input name="start_date" type="date" value={form.start_date} onChange={handleChange} className="form-input" />
          </FormField>
        </div>
        <FormField label="Bio">
          <textarea name="bio" value={form.bio} onChange={handleChange} className="form-input resize-none h-20" />
        </FormField>
      </FormSection>

      <FormSection icon={<Briefcase className="h-3.5 w-3.5" />} title="Skills & Languages">
        <FormField label="Skills" hint="Comma-separated">
          <input name="skills" value={form.skills} onChange={handleChange} className="form-input" placeholder="TypeScript, React" />
        </FormField>
        <FormField label="Languages" hint="Comma-separated">
          <input name="languages" value={form.languages} onChange={handleChange} className="form-input" placeholder="English, Spanish" />
        </FormField>
      </FormSection>

      <FormSection icon={<MapPin className="h-3.5 w-3.5" />} title="Location">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="City">
            <input name="city" value={form.city} onChange={handleChange} className="form-input" />
          </FormField>
          <FormField label="Country" required>
            <input name="country" value={form.country} onChange={handleChange} className="form-input" required />
          </FormField>
          <FormField label="Latitude" hint={isGeocoding ? 'detecting…' : 'auto-filled from city'}>
            <div className="relative">
              <input name="latitude" type="number" step="any" value={form.latitude} onChange={handleChange} className="form-input pr-8" />
              {isGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
            </div>
          </FormField>
          <FormField label="Longitude" hint={isGeocoding ? 'detecting…' : 'auto-filled from city'}>
            <div className="relative">
              <input name="longitude" type="number" step="any" value={form.longitude} onChange={handleChange} className="form-input pr-8" />
              {isGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
            </div>
          </FormField>
          <FormField label="Timezone" hint={isGeocoding ? 'detecting…' : 'auto-filled from city'} required>
            <div className="relative">
              <input name="timezone" value={form.timezone} onChange={handleChange} className="form-input pr-8" placeholder="America/New_York" required />
              {isGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
            </div>
          </FormField>
        </div>
        {form.latitude && form.longitude && (
          <LocationPreview
            lat={parseFloat(form.latitude)}
            lon={parseFloat(form.longitude)}
            country={form.country}
          />
        )}
      </FormSection>

      <FormSection icon={<Globe className="h-3.5 w-3.5" />} title="Social Media">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="GitHub username">
            <input name="social_github" value={form.social_github} onChange={handleChange} className="form-input" placeholder="username" />
          </FormField>
          <FormField label="LinkedIn handle">
            <input name="social_linkedin" value={form.social_linkedin} onChange={handleChange} className="form-input" placeholder="in/handle" />
          </FormField>
          <FormField label="Twitter / X handle">
            <input name="social_twitter" value={form.social_twitter} onChange={handleChange} className="form-input" placeholder="@handle" />
          </FormField>
          <FormField label="Slack display name">
            <input name="social_slack" value={form.social_slack} onChange={handleChange} className="form-input" />
          </FormField>
        </div>
      </FormSection>
    </>
  );
};

const TabButton = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
      active
        ? 'border-primary-500 text-primary-400'
        : 'border-transparent text-bolt-textMuted hover:text-bolt-text hover:border-bolt-border'
    }`}
  >
    {children}
  </button>
);

const FormSection = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <span className="text-bolt-textSubtle">{icon}</span>
      <span className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle">{title}</span>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const FormField = ({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs text-bolt-textMuted mb-1">
      {label}
      {required && <span className="text-error-400 ml-0.5">*</span>}
      {hint && <span className="ml-1 text-bolt-textSubtle">({hint})</span>}
    </label>
    {children}
  </div>
);

// ─── Teams Tab ────────────────────────────────────────────────────────────────

const TeamsTab = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState<{ iconType: Team['iconType']; iconValue: string }>({ iconType: 'emoji', iconValue: '' });
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState<{ iconType: Team['iconType']; iconValue: string }>({ iconType: 'emoji', iconValue: '' });
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [confirmDeleteTeam, setConfirmDeleteTeam] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchAllTeams(),
      supabase.from('employee_teams').select('team_id'),
    ]).then(([teamsData, membersData]) => {
      setTeams(teamsData);
      const counts: Record<string, number> = {};
      for (const row of membersData.data ?? []) {
        counts[row.team_id] = (counts[row.team_id] ?? 0) + 1;
      }
      setMemberCounts(counts);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) { setAddError('Team name is required.'); return; }
    if (!newIcon.iconValue) { setAddError('Please select an icon.'); return; }
    setAddError(null);
    setIsAdding(true);
    try {
      const team = await createTeam(newName, newIcon.iconType, newIcon.iconValue);
      setTeams(prev => [...prev, team].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
      setNewIcon({ iconType: 'emoji', iconValue: '' });
      setShowAddForm(false);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to create team.');
    } finally {
      setIsAdding(false);
    }
  };

  const openEdit = (team: Team) => {
    setEditingTeam(team);
    setEditName(team.name);
    setEditIcon({ iconType: team.iconType, iconValue: team.iconValue });
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingTeam) return;
    if (!editName.trim()) { setEditError('Team name is required.'); return; }
    if (!editIcon.iconValue) { setEditError('Please select an icon.'); return; }
    setEditError(null);
    setIsSavingEdit(true);
    try {
      const updated = await updateTeam(editingTeam.id, { name: editName, iconType: editIcon.iconType, iconValue: editIcon.iconValue });
      setTeams(prev => prev.map(t => t.id === updated.id ? updated : t));
      setEditingTeam(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update team.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteTeam) return;
    setIsDeleting(true);
    try {
      await deleteTeam(confirmDeleteTeam.id);
      setTeams(prev => prev.filter(t => t.id !== confirmDeleteTeam.id));
      setMemberCounts(prev => { const c = { ...prev }; delete c[confirmDeleteTeam.id]; return c; });
      setConfirmDeleteTeam(null);
    } catch {
      // ignore
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-bolt-text">Teams</h1>
          <p className="text-xs text-bolt-textMuted">{teams.length} team{teams.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { setShowAddForm(v => !v); setAddError(null); }}
          className="btn btn-primary text-xs px-3 py-1.5 gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add team
        </button>
      </div>

      {showAddForm && (
        <div className="card p-4 border-primary-600/30 bg-primary-600/5">
          <h3 className="text-xs font-semibold text-bolt-text mb-3">New team</h3>
          <form onSubmit={handleAdd} className="space-y-3">
            <FormField label="Team name" required>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                className="form-input"
                placeholder="e.g. Team Rust"
              />
            </FormField>
            <div>
              <label className="block text-xs text-bolt-textMuted mb-1.5">Icon</label>
              <TeamIconPicker value={newIcon} onChange={setNewIcon} />
            </div>
            {addError && (
              <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">{addError}</p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary text-xs px-3 py-1.5">Cancel</button>
              <button type="submit" disabled={isAdding} className="btn btn-primary text-xs px-3 py-1.5 gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {isAdding ? 'Creating...' : 'Create team'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-10 text-center text-bolt-textSubtle text-xs">Loading...</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bolt-border">
                <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Team</th>
                <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium hidden sm:table-cell">Icon type</th>
                <th className="text-left px-4 py-3 text-bolt-textSubtle font-medium">Members</th>
                <th className="w-24 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {teams.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-bolt-textSubtle">No teams yet.</td>
                </tr>
              )}
              {teams.map(team => (
                <tr key={team.id} className="border-b border-bolt-border/50 hover:bg-bolt-surfaceHover transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-bolt-bg border border-bolt-border flex items-center justify-center flex-shrink-0">
                        <TeamIcon team={team} size={16} className="text-bolt-textMuted" />
                      </div>
                      <span className="font-medium text-bolt-text">{team.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="capitalize text-bolt-textMuted">{team.iconType}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-bolt-textMuted">{memberCounts[team.id] ?? 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEdit(team)}
                        className="p-1.5 rounded-md text-bolt-textSubtle hover:text-bolt-text hover:bg-bolt-bg transition-colors"
                        title="Edit team"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteTeam(team)}
                        className="p-1.5 rounded-md text-bolt-textSubtle hover:text-error-400 hover:bg-error-500/10 transition-colors"
                        title="Delete team"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit team modal */}
      {editingTeam && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md p-6 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-bolt-text">Edit team</h3>
              <button onClick={() => setEditingTeam(null)} className="text-bolt-textSubtle hover:text-bolt-text transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <FormField label="Team name" required>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="form-input"
                />
              </FormField>
              <div>
                <label className="block text-xs text-bolt-textMuted mb-1.5">Icon</label>
                <TeamIconPicker value={editIcon} onChange={setEditIcon} teamId={editingTeam.id} />
              </div>
              {editError && (
                <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">{editError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditingTeam(null)} className="btn btn-secondary text-xs px-4 py-2">Cancel</button>
              <button
                onClick={handleSaveEdit}
                disabled={isSavingEdit}
                className="btn btn-primary text-xs px-4 py-2 gap-1.5"
              >
                <Save className="h-3.5 w-3.5" />
                {isSavingEdit ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDeleteTeam && (
        <DeleteModal
          name={confirmDeleteTeam.name}
          description={`This will permanently delete the team and remove all ${memberCounts[confirmDeleteTeam.id] ?? 0} member association${(memberCounts[confirmDeleteTeam.id] ?? 0) !== 1 ? 's' : ''}. This cannot be undone.`}
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteTeam(null)}
        />
      )}
    </div>
  );
};

const DeleteModal = ({ name, description, isDeleting, onConfirm, onCancel }: {
  name: string; description: string; isDeleting: boolean; onConfirm: () => void; onCancel: () => void;
}) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="card w-full max-w-md p-6 space-y-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-error-500/15 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="h-4 w-4 text-error-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-bolt-text truncate">{name}</h3>
          <p className="text-xs text-bolt-textMuted mt-1">{description}</p>
        </div>
        <button onClick={onCancel} className="text-bolt-textSubtle hover:text-bolt-text transition-colors flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="btn btn-secondary text-xs px-4 py-2">Cancel</button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="btn text-xs px-4 py-2 bg-error-600 hover:bg-error-500 text-white border-transparent"
        >
          {isDeleting ? 'Removing...' : 'Remove'}
        </button>
      </div>
    </div>
  </div>
);

// ─── HQ TAB ──────────────────────────────────────────────────────────────────

interface HQMarkerDraggerProps {
  position: [number, number];
  onChange: (lat: number, lng: number) => void;
}

function HQMarkerDragger({ position, onChange }: HQMarkerDraggerProps) {
  const hqIcon = L.divIcon({
    html: `<div style="width:36px;height:36px;border-radius:50%;background:#0f172a;border:2px solid #38bdf8;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5);">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
        <path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
      </svg>
    </div>`,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

  const marker = L.marker(position, { draggable: true, icon: hqIcon });

  const MapInner = () => {
    useMapEvents({});
    return (
      <Marker
        position={position}
        icon={hqIcon}
        draggable
        eventHandlers={{
          dragend: (e) => {
            const latlng = (e.target as L.Marker).getLatLng();
            onChange(latlng.lat, latlng.lng);
          },
        }}
      />
    );
  };
  void marker;
  return <MapInner />;
}

function HQTab() {
  const { hq, reload } = useHQ();
  const { theme } = useTheme();
  const [address, setAddress] = useState('');
  const [name, setName] = useState(hq?.name ?? '');
  const [resolvedAddress, setResolvedAddress] = useState(hq?.address ?? '');
  const [lat, setLat] = useState<number | null>(hq?.latitude ?? null);
  const [lng, setLng] = useState<number | null>(hq?.longitude ?? null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (hq) {
      setName(hq.name);
      setResolvedAddress(hq.address);
      setLat(hq.latitude);
      setLng(hq.longitude);
    }
  }, [hq]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSearch = async () => {
    if (!address.trim()) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address.trim())}&format=json&limit=1&addressdetails=1`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const results = await res.json();
      if (!results.length) { showToast('error', 'No results found'); return; }
      const r = results[0];
      setLat(parseFloat(r.lat));
      setLng(parseFloat(r.lon));
      setResolvedAddress(r.display_name);
      if (!name) setName(r.display_name.split(',')[0]);
    } catch {
      showToast('error', 'Geocoding failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (lat === null || lng === null || !name.trim() || !resolvedAddress) {
      showToast('error', 'Please search for an address and set a name first');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('app_settings').upsert(
      { key: 'hq', value: { name: name.trim(), address: resolvedAddress, latitude: lat, longitude: lng } },
      { onConflict: 'key' }
    );
    setSaving(false);
    if (error) { showToast('error', error.message); return; }
    await reload();
    showToast('success', 'HQ saved');
  };

  const handleClear = async () => {
    setSaving(true);
    const { error } = await supabase.from('app_settings').upsert(
      { key: 'hq', value: null },
      { onConflict: 'key' }
    );
    setSaving(false);
    if (error) { showToast('error', error.message); return; }
    setLat(null); setLng(null); setResolvedAddress(''); setName(''); setAddress('');
    await reload();
    showToast('success', 'HQ removed');
  };

  const hasPin = lat !== null && lng !== null;

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm border ${
          toast.type === 'success'
            ? 'bg-success-500/10 border-success-500/30 text-success-400'
            : 'bg-error-500/10 border-error-500/30 text-error-400'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
          {toast.message}
        </div>
      )}

      <div className="card p-5 space-y-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-600/15 rounded-lg text-primary-400">
            <Building2 className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-bolt-text">Company HQ</h2>
            <p className="text-xs text-bolt-textSubtle mt-0.5">
              Pin your headquarters on the map so team members can see how far they are.
            </p>
          </div>
          {hq && (
            <span className="ml-auto text-xs font-medium px-2.5 py-1 rounded-full bg-success-500/10 border border-success-500/25 text-success-400">
              Configured
            </span>
          )}
        </div>

        {/* Address search */}
        <div>
          <label className="block text-xs font-medium text-bolt-textMuted mb-1.5">Search for address</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="e.g. 225 Bush Street, San Francisco"
              className="form-input flex-1"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !address.trim()}
              className="btn btn-primary gap-2"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </button>
          </div>
        </div>

        {/* Map */}
        {hasPin && (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-bolt-textMuted">
              Drag the pin to fine-tune the exact position
            </label>
            <div className="rounded-xl overflow-hidden border border-bolt-border" style={{ height: 320 }}>
              <MapContainer
                key={`${lat}-${lng}`}
                center={[lat!, lng!]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution={getBasemapAttribution()}
                  url={getBasemapUrl(theme)}
                />
                <HQMarkerDragger
                  position={[lat!, lng!]}
                  onChange={(newLat, newLng) => { setLat(newLat); setLng(newLng); }}
                />
              </MapContainer>
            </div>

            <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-bolt-textSubtle mt-0.5 shrink-0" />
                <p className="text-xs text-bolt-textMuted break-all">{resolvedAddress}</p>
              </div>
              <p className="text-xs text-bolt-textSubtle font-mono">
                {lat!.toFixed(6)}, {lng!.toFixed(6)}
              </p>
            </div>
          </div>
        )}

        {/* HQ Name */}
        <div>
          <label className="block text-xs font-medium text-bolt-textMuted mb-1.5">HQ Label</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. San Francisco HQ"
            className="form-input"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleSave}
            disabled={saving || !hasPin || !name.trim()}
            className="btn btn-primary gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save HQ
          </button>
          {hq && (
            <button
              onClick={handleClear}
              disabled={saving}
              className="btn btn-secondary gap-2 text-error-400"
            >
              <Trash2 className="h-4 w-4" />
              Clear HQ
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DebugTab() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
  const supabaseProjectId = supabaseUrl ? supabaseUrl.replace('https://', '').split('.')[0] : '';
  const sentryDsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim();
  const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();
  const cartoApiKey = (import.meta.env.VITE_CARTO_API_KEY ?? '').trim();
  const gitSha = typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : '';

  const items = [
    { label: 'Git SHA', value: gitSha },
    { label: 'Sentry DSN', value: sentryDsn },
    { label: 'Google OAuth Client ID', value: googleClientId },
    { label: 'CARTO API Key', value: cartoApiKey ? `${cartoApiKey.slice(0, 4)}…` : '' },
    { label: 'Supabase Project ID', value: supabaseProjectId },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-bolt-text">Debug Info</h1>
        <p className="text-xs text-bolt-textMuted">Build configuration and environment diagnostics.</p>
      </div>

      <div className="card divide-y divide-bolt-border">
        {items.map(({ label, value }) => (
          <div key={label} className="flex items-start justify-between gap-4 px-4 py-3">
            <span className="text-xs font-medium text-bolt-textMuted whitespace-nowrap">{label}</span>
            {value ? (
              <span className="text-xs font-mono text-right break-all text-bolt-text">{value}</span>
            ) : (
              <span className="text-xs font-mono text-warning-400">not configured</span>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-bolt-textSubtle">
        Environment variables prefixed with VITE_ must be set in the project secrets to appear here at build time.
      </p>
    </div>
  );
}

export default AdminPage;